// src/components/Subscription/SubscriptionSuccess.js
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper } from '@mui/material';
import { CheckCircle } from 'lucide-react';
import { HEALTH_GREEN } from '../Login/utils/mockData';
import agthealthService from '../../services/agthealthService';
import { useSubscription } from '../../contexts/context/SubscriptionContext';

const SubscriptionSuccess = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshStatus } = useSubscription(); // Agregar acceso al contexto
  
  useEffect(() => {
    const processSubscription = async () => {
      try {
        // Obtener el ID de sesión de la URL
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        
        if (!sessionId) {
          setError('No se encontró ID de sesión');
          setIsLoading(false);
          return;
        }
        
        // Procesar el éxito de la suscripción
        const response = await agthealthService.processSubscriptionSuccess(sessionId);
        
        if (response.success) {
          // Actualizar el token si se recibió uno nuevo
          if (response.data.token) {
            localStorage.setItem('agthealth_token', response.data.token);
            console.log('Token actualizado con isPro=true');
            
            // Disparar evento personalizado para que otros componentes sepan del cambio
            window.dispatchEvent(new CustomEvent('tokenUpdated'));
          }
          
          // IMPORTANTE: Refrescar el estado de suscripción inmediatamente
          await refreshStatus();
        
          // Esperar un momento y redirigir al dashboard
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000); // Reducir tiempo de espera
        } else {
          throw new Error(response.message || 'Error al procesar la suscripción');
        }
      } catch (err) {
        console.error('Error al procesar suscripción:', err);
        setError(err.response?.data?.message || 'Error al procesar la suscripción');
      } finally {
        setIsLoading(false);
      }
    };
    
    processSubscription();
  }, [location, navigate, refreshStatus]);
  
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      p: 2
    }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%', textAlign: 'center' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6">Procesando tu suscripción...</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Esto puede tomar unos segundos
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/dashboard')}
            >
              Volver al Dashboard
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CheckCircle size={60} color={HEALTH_GREEN} style={{ marginBottom: 16 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              ¡Suscripción Activada!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Tu suscripción a AGT Health Pro ha sido activada con éxito. Ahora puedes disfrutar de todas las funcionalidades.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Serás redirigido al dashboard automáticamente...
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/dashboard')}
              sx={{
                bgcolor: HEALTH_GREEN,
                '&:hover': {
                  bgcolor: '#0e8a5f'
                }
              }}
            >
              Ir al Dashboard Ahora
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SubscriptionSuccess;